'use client'

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel'
import { useFeatureLibrary } from '@/hooks/common/use-feature-library'
import { Link as I18nLink } from '@/i18n/navigation'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { HeroBanner as HeroBannerType, ProductTab } from '@/types/coolxprint2'
import { FeaturesLibrarySection, HeroFeatureCard } from '@/types/cxp-solution-page'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import Link from 'next/link'
import { CategoryQuickTabs } from './category-quick-tabs'

const DEFAULT_BANNERS = [
  {
    desktop: '/image/placeholder-hero-banner.jpg',
    mobile: '/image/placeholder-hero-banner-mobile.jpg',
    alt: 'CXP Hero Banner',
  },
]

interface HeroBannerProps {
  banners?: HeroBannerType[]
  tabs?: ProductTab[]
  featureCards?: HeroFeatureCard[]
  featuresLibrary?: FeaturesLibrarySection
}

export function HeroBanner({
  banners,
  tabs,
  featureCards,
  featuresLibrary,
}: HeroBannerProps) {
  const { getFeature } = useFeatureLibrary({ featuresLibrary })
  // Filter active banners and sort by order
  const activeBanners =
    banners
      ?.filter((banner) => banner.is_active)
      .sort((a, b) => a.order - b.order) || []

  // Use API data if available, otherwise use default
  const displayBanners =
    activeBanners.length > 0
      ? activeBanners.map((banner) => ({
          desktop: banner.desktop_image,
          mobile: banner.mobile_image || banner.desktop_image,
          alt: banner.alt_text,
          link: banner.link || undefined,
        }))
      : DEFAULT_BANNERS.map((banner) => ({
          ...banner,
          link: undefined,
        }))

  return (
    <section
      className={`relative w-full overflow-visible max-sm:min-h-[500px] lg:min-h-[500px] ${
        tabs && tabs.length > 0 ? 'bg-neutral-50 pb-20 md:pb-24' : ''
      }`}
      aria-label='Hero Banner Slideshow'
    >
      <h2 className='sr-only'>CXP Hero Banner</h2>
      <Carousel
        opts={{
          loop: true,
        }}
        plugins={[
          Autoplay({
            delay: 5000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]}
      >
        <CarouselContent className='ml-0 h-full w-full'>
          {displayBanners.map((banner, index) => {
            const content = (
              <picture className='block h-full w-full'>
                <source
                  srcSet={ImageHelper.getImageUrl(banner.mobile)}
                  media='(max-width: 991px)'
                />
                <Image
                  src={ImageHelper.getImageUrl(banner.desktop)}
                  alt={banner.alt}
                  width={1920}
                  height={787}
                  className='h-full w-full object-cover'
                  priority={index === 0}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  sizes='100vw'
                />
              </picture>
            )

            return (
              <CarouselItem
                key={index}
                className='relative h-full w-full p-0'
                role='region'
                aria-label={`Hero Banner ${index + 1} of ${displayBanners.length}`}
              >
                {banner.link ? (
                  <Link href={banner.link} className='block h-full w-full'>
                    {content}
                  </Link>
                ) : (
                  content
                )}
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <CarouselNavigation />
      </Carousel>
      {tabs && tabs.length > 0 && <CategoryQuickTabs tabs={tabs} />}

      {/* Feature Cards - Outside Container để có z-index độc lập */}
      {featureCards && featureCards.length > 0 && (
        <div className='absolute bottom-0 left-1/2 top-1/2 z-20 w-full -translate-x-1/2 translate-y-[60%] px-4 md:px-0'>
          <div
            className='mx-auto flex w-full max-w-7xl gap-3 overflow-x-auto md:justify-center md:gap-4'
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <style
              dangerouslySetInnerHTML={{
                __html: `
              .feature-cards-scroll::-webkit-scrollbar {
                display: none;
              }
            `,
              }}
            />
            <div className='feature-cards-scroll flex flex-nowrap gap-3 md:gap-4'>
              {featureCards.map((card, index) => {
                const arrowColor = card.arrow_color || 'black'
                const isOrange = arrowColor === 'orange'

                const feature = card.icon ? getFeature(card.icon) : null
                const iconUrl = feature ? feature.icon : card.icon

                const cardContent = (
                  <div className='group flex min-w-[140px] flex-shrink-0 items-center gap-3 rounded-xl bg-white px-4 py-3 transition-all md:min-w-[180px] md:gap-4 md:px-6 md:py-4'>
                    {/* Icon */}
                    {iconUrl ? (
                      <div className='relative h-8 w-8 shrink-0 md:h-10 md:w-10'>
                        <Image
                          src={ImageHelper.getImageUrl(iconUrl)}
                          alt={card.title}
                          fill
                          className='object-contain transition-opacity group-hover:opacity-80'
                        />
                      </div>
                    ) : (
                      <div className='h-8 w-8 shrink-0 rounded bg-neutral-200 transition-opacity group-hover:opacity-80 md:h-10 md:w-10' />
                    )}

                    {/* Title */}
                    <span
                      className={cn(
                        'flex-1 font-formaDjr text-sm font-medium md:text-base',
                        isOrange
                          ? 'text-[#ff4e00] group-hover:text-[#ff4e00]'
                          : 'text-neutral-900 group-hover:text-[#ff4e00]'
                      )}
                      dangerouslySetInnerHTML={{ __html: card.title }}
                    />

                    {/* Arrow Icon */}
                    <div className='flex-shrink-0'>
                      <div
                        className={cn(
                          'flex h-7 w-7 items-center justify-center rounded-lg border transition-colors md:h-8 md:w-8',
                          isOrange
                            ? 'border-[#ff4e00] bg-[#ff4e00] group-hover:border-[#ff4e00] group-hover:bg-[#ff4e00]'
                            : 'border-neutral-900 group-hover:border-[#ff4e00] group-hover:bg-[#ff4e00]'
                        )}
                      >
                        <svg
                          width='12'
                          height='12'
                          viewBox='0 0 20 20'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                          className={cn(
                            'h-4 w-4 md:h-5 md:w-5',
                            isOrange
                              ? 'text-white'
                              : 'text-neutral-900 transition-colors group-hover:text-white'
                          )}
                        >
                          <path
                            d='M7.5 15L12.5 10L7.5 5'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                )

                if (card.link) {
                  return (
                    <I18nLink
                      key={index}
                      href={card.link}
                      {...(card.ga_tracking_value &&
                        card.ga_tracking_label && {
                          'ga-tracking-value': card.ga_tracking_value,
                          'ga-tracking-label': card.ga_tracking_label,
                        })}
                    >
                      {cardContent}
                    </I18nLink>
                  )
                }

                return <div key={index}>{cardContent}</div>
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2 border-none invert max-lg:hidden lg:left-1'
        variant='outline-neutral'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2 border-none invert max-lg:hidden lg:right-1'
        variant='outline-neutral'
        hideIfEnd
      />
    </>
  )
}
